import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { fetchAdminUsersAction } from "../slices/adminUsersSlice";
import { RootState } from "@/app/rootReducer";
import { FetchAdminUsersRequest } from "../api/fetchAdminUsers";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

// Helper to read initial state from URL
function getInitialStateFromUrl(searchParams: URLSearchParams) {
  const page = searchParams.get("page");
  const pageSize = searchParams.get("pageSize");
  const sortBy = searchParams.get("sortBy");
  const sortOrder = searchParams.get("sortOrder");
  const businessRoleId = searchParams.get("businessRoleId");

  return {
    pagination: {
      pageIndex: page ? parseInt(page, 10) - 1 : 0,
      pageSize: pageSize ? parseInt(pageSize, 10) : 10,
    },
    sorting: sortBy ? [{ id: sortBy, desc: sortOrder === "DESC" }] : [],
    filters: {
      firstName: searchParams.get("firstName") || undefined,
      lastName: searchParams.get("lastName") || undefined,
      email: searchParams.get("email") || undefined,
      salesforceId: searchParams.get("salesforceId") || undefined,
      ssoId: searchParams.get("ssoId") || undefined,
      companyUuid: searchParams.get("companyUuid") || undefined,
      businessRoleId: businessRoleId ? parseInt(businessRoleId, 10) : undefined,
    },
  };
}

export function useAdminUsers() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { users, totalCount, loading, error } = useSelector(
    (state: RootState) => state.adminUsers,
  );

  // Initialize state from URL params (only on mount)
  const initialState = React.useMemo(
    () => getInitialStateFromUrl(searchParams),
    []
  );

  const [pagination, setPagination] = useState<PaginationState>(initialState.pagination);

  const [sorting, setSorting] = useState<SortingState>(initialState.sorting);

  const [filters, setFilters] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    salesforceId?: string;
    ssoId?: string;
    companyUuid?: string;
    businessRoleId?: number;
  }>(initialState.filters);

  const requestData = React.useMemo<FetchAdminUsersRequest>(() => {
    return {
      page: pagination.pageIndex + 1,
      sortBy: sorting.length > 0 ? sorting[0].id : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : undefined,
      firstName: filters.firstName,
      lastName: filters.lastName,
      email: filters.email,
      salesforceId: filters.salesforceId,
      ssoId: filters.ssoId,
      companyUuid: filters.companyUuid,
      businessRoleId: filters.businessRoleId,
      pageSize: pagination.pageSize,
    };
  }, [pagination, sorting, filters]);

  useEffect(() => {
    dispatch(fetchAdminUsersAction(requestData));
  }, [dispatch, requestData]);

  // Sync state to URL whenever it changes
  useEffect(() => {
    const params = new URLSearchParams();

    // Pagination
    if (pagination.pageIndex > 0) {
      params.set("page", (pagination.pageIndex + 1).toString());
    }
    if (pagination.pageSize !== 10) {
      params.set("pageSize", pagination.pageSize.toString());
    }

    // Sorting
    if (sorting.length > 0) {
      params.set("sortBy", sorting[0].id);
      params.set("sortOrder", sorting[0].desc ? "DESC" : "ASC");
    }

    // Filters
    if (filters.firstName) params.set("firstName", filters.firstName);
    if (filters.lastName) params.set("lastName", filters.lastName);
    if (filters.email) params.set("email", filters.email);
    if (filters.salesforceId) params.set("salesforceId", filters.salesforceId);
    if (filters.ssoId) params.set("ssoId", filters.ssoId);
    if (filters.companyUuid) params.set("companyUuid", filters.companyUuid);
    if (filters.businessRoleId !== undefined) {
      params.set("businessRoleId", filters.businessRoleId.toString());
    }

    setSearchParams(params, { replace: true });
  }, [pagination, sorting, filters, setSearchParams]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleFilterChange = (filterKey: string, value: string | number | undefined) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterKey]: value,
    }));
  };

  return {
    users,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
